import React, { useEffect, useState } from 'react'
import {
  Instructions,
  Interface,
  SuccessDialog,
  useShowInfo,
  useContainerStyles
} from '../ui'
import {
  useViewerZoom,
  useViewerRotation
} from '../model-viewer'
import {
  usePuzzleConfig,
  usePuzzlesReset,
  PuzzleCanvas,
  isComplete
} from '../puzzle'
import { puzzles } from './config/puzzles'

/**
 * Returns the original state of the puzzle
 * @param {*} state
 */
const getResetPuzzle = (puzzlesReset, active) => {
  const restorePuzzle = puzzlesReset.find(
    (p) => p.id === active.id
  )
  return JSON.parse(JSON.stringify(restorePuzzle))
}

function PuzzleInstructions(props) {
  return (
    <Instructions
      description="Assemble the pieces to build the fossils"
      steps={[
        'Choose one of the trilobites from the selection menu above',
        'Press on the pieces and move your finger to drag them into place',
        'Once the pieces are connected, press on the fossil and move your finger to rotate',
        'Use the zoom slider on the right to zoom in and out'
      ]}
    />
  )
}

function TrilobitesPuzzleApp({ ...props }) {
  /** store initial state of all puzzles for reset */
  const resetPuzzleConfig = usePuzzlesReset(puzzles)

  /** configuration for current puzzle and setter */
  const [active, setPuzzle] = usePuzzleConfig(puzzles[0])

  /** zoom level for current puzzle */
  const [zoom, setZoom] = useViewerZoom(1)

  /** rotation of the current puzzle */
  const [rotation, setRotation] = useViewerRotation()

  /** boolean toggle for show info */
  const [showInfo, toggleShowInfo] = useShowInfo(true)

  /** boolean to determine if current puzzle is complete */
  const complete = isComplete(active)

  /** boolean to determine if success dialog is open */
  const [showDialog, setShowDialog] = useState(false)

  /** handler function to reset puzzle from interface */
  const handleReset = () => {
    const resetPuzzle = getResetPuzzle(
      resetPuzzleConfig,
      active
    )
    const puzzleIndex = puzzles.findIndex(
      (p) => p.id === active.id
    )
    puzzles[puzzleIndex] = resetPuzzle
    setPuzzle(puzzles[puzzleIndex])
  }

  /** handler function to set active puzzle from interface */
  const handlePuzzleChange = (e) => {
    const id = e.target.value
    const puzzleIndex = puzzles.findIndex(
      (p) => p.id === id
    )
    setPuzzle(puzzles[puzzleIndex])
  }

  /** Show dialog once the puzzle is complete */
  useEffect(() => {
    if (complete) {
      setShowDialog(true)
    }
  }, [complete])

  /** Set puzzle as null on unmount */
  useEffect(() => {
    return () => {
      setPuzzle(null)
    }
    // eslint-disable-next-line
  }, [])

  /** Use styles for canvas */
  const classes = useContainerStyles({
    shifted: showInfo
  })

  return active ? (
    <div className="app" {...props}>
      <PuzzleCanvas
        className={classes.root}
        enableWheel={true}
        puzzle={active}
        zoom={zoom}
        rotation={rotation}
        complete={complete}
        setRotation={setRotation}
        setZoom={setZoom}
      />
      <Interface
        items={puzzles}
        itemTitle="Current Puzzle"
        activeItem={active}
        showInfo={showInfo}
        zoom={zoom}
        complete={complete}
        HelpComponent={<PuzzleInstructions />}
        onReset={handleReset}
        onShowInfo={toggleShowInfo}
        onSetActiveItem={handlePuzzleChange}
        onZoom={setZoom}
      />
      <SuccessDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
      />
    </div>
  ) : null
}

export default TrilobitesPuzzleApp
